/**
 * KIOSK PRIMITIVES — the whole product's control vocabulary, in one file.
 *
 * The brief, verbatim: "the home screen should look like a limited kiosk, may even run it on
 * a touch screen — controls can be brought up in modals, sliders, up and down options, pop up
 * keyboard if necessary — super intuitive, simple. You keep making shit that looks like a
 * fighter pilot cockpit instrument board. We're answering after hour calls here."
 *
 * So these are components, not a stylesheet applied per screen. Nineteen screens converted by
 * hand would drift within a week, and the thing this product is measured against fails exactly
 * that way — it builds the right pattern once and never generalises it. A screen here should be
 * a short list of these, arranged.
 *
 * THE RULES THEY CARRY:
 *   · Nothing pressable under 56px. Fingers, not cursors.
 *   · A switch that is ON inverts to a solid plane. It never gets a colour, a chip or a dot —
 *     colour means "a person is needed" and nothing else may wear it.
 *   · Every control is a stepper, a slider or a keypad, opened in a sheet. A text field has to
 *     argue for itself.
 *   · Every sheet says what happens BEFORE you commit, in the words a caller would hear.
 */
import { useEffect, useRef } from 'react';
import type { ReactElement, ReactNode } from 'react';

/* ── shell ────────────────────────────────────────────────────────────────── */

export function KioskPage({ children }: { children: ReactNode }): ReactElement {
  return <div className="k-page">{children}</div>;
}

export function Panel({ children }: { children: ReactNode }): ReactElement {
  return <section className="k-panel">{children}</section>;
}

/**
 * A horizontal band inside a panel. Bands stack; they never nest.
 *
 * `help` takes an element — a HelpTip, usually — and sits it beside the label at a proper
 * target size. Three screens hand-rolled exactly this (`.ndo-head`, `.ndp-head`, `.nds-head`)
 * and ten more had nowhere to put a "?" at all, so several tips ended up as trailing body
 * text pointing at a control they were meant to be attached to.
 */
export function Band({
  label,
  help,
  children,
}: {
  label?: ReactNode;
  help?: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="k-band">
      {label ? (
        help ? (
          <div className="k-head">
            <div className="k-lbl">{label}</div>
            {help}
          </div>
        ) : (
          <div className="k-lbl">{label}</div>
        )
      ) : null}
      {children}
    </div>
  );
}

/**
 * A row of big buttons, wrapping. Six screens invented this independently — `.ndo-row`,
 * `.ndp-row`, `.nds-row`, `.ndc-row`, `.ndp-acts` and two anonymous inline flex objects —
 * which is about as clear a signal as a layer ever gets. A bare paragraph dropped in beside
 * the buttons becomes the explanation that shares the row.
 */
export function Row({ children }: { children: ReactNode }): ReactElement {
  return <div className="k-row">{children}</div>;
}

/**
 * Static label-over-value pairs. The most-duplicated missing thing in the whole conversion:
 * nine screens needed it and every one of them wrote down the same reason — ListItem is a
 * <button>, these are not pressable, and a dead button on a touch screen is a lie.
 */
export function Facts({ children }: { children: ReactNode }): ReactElement {
  return <div className="k-facts">{children}</div>;
}

export function Fact({
  label,
  value,
  detail,
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
}): ReactElement {
  return (
    <div className="k-fact">
      <div className="k-lbl">{label}</div>
      <b>{value}</b>
      {detail ? <small>{detail}</small> : null}
    </div>
  );
}

/**
 * Nothing is here, said at kiosk scale. The console EmptyState is 14px over 13px and reads
 * as a second panel nested inside a band; four screens wrote a local replacement rather than
 * ship type under the floor.
 */
export function Nothing({ title, children }: { title: string; children?: ReactNode }): ReactElement {
  return (
    <div className="k-nothing">
      <b>{title}</b>
      {children ? <p>{children}</p> : null}
    </div>
  );
}

/* ── the answer ───────────────────────────────────────────────────────────── */

/**
 * The one line a screen exists to say. It is the first thing rendered and the only thing
 * sized like a headline, because at 2 AM the question is always the same: do I have to get up.
 */
export function Answer({
  needs,
  title,
  detail,
  action,
}: {
  needs: boolean;
  /* ReactNode, not string: three screens needed a figure or a <Money> inside the headline. */
  title: ReactNode;
  detail?: ReactNode;
  action?: ReactNode;
}): ReactElement {
  return (
    <div className="k-band">
      <div className="k-answer" data-needs={needs ? 'true' : undefined}>
        <span className="lamp" aria-hidden="true" />
        <div>
          <h2>{title}</h2>
          {detail ? <p>{detail}</p> : null}
        </div>
        {action}
      </div>
    </div>
  );
}

/* ── buttons ──────────────────────────────────────────────────────────────── */

export type BigButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  kind?: 'plain' | 'go' | 'need';
  /** 'hear' is the tall play button that sits beside a quotation. */
  size?: 'default' | 'hear';
  disabled?: boolean;
  /**
   * Working. Four screens faked this with `disabled` and a swapped label, and one of them
   * was a button that spends money and takes seconds — a control that does not change while
   * you wait invites a second press.
   */
  busy?: boolean;
  play?: boolean;
  type?: 'button' | 'submit';
};

export function BigButton({
  children,
  onClick,
  kind = 'plain',
  size = 'default',
  disabled = false,
  busy = false,
  play = false,
  type = 'button',
}: BigButtonProps): ReactElement {
  return (
    <button
      type={type}
      className="k-btn"
      data-kind={kind === 'plain' ? undefined : kind}
      data-size={size === 'default' ? undefined : size}
      data-busy={busy ? 'true' : undefined}
      aria-busy={busy || undefined}
      onClick={onClick}
      disabled={disabled || busy}
    >
      {play ? <span className="tri" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

/* ── switches ─────────────────────────────────────────────────────────────── */

export type SwitchTileProps = {
  name: string;
  /** One short line: what it is, or since when. Never a sentence. */
  detail?: string;
  on?: boolean;
  /** True for something that is the case and that a person should know about. */
  alert?: boolean;
  disabled?: boolean;
  onPress?: () => void;
};

export function SwitchTile({ name, detail, on = false, alert = false, disabled = false, onPress }: SwitchTileProps): ReactElement {
  return (
    <button
      type="button"
      className="k-sw"
      data-on={on ? 'true' : undefined}
      data-alert={alert && !on ? 'true' : undefined}
      aria-pressed={on}
      disabled={disabled}
      onClick={onPress}
    >
      <b>{name}</b>
      {detail ? <small>{detail}</small> : null}
    </button>
  );
}

export function Switches({ children }: { children: ReactNode }): ReactElement {
  return <div className="k-switches">{children}</div>;
}

/* ── doors ────────────────────────────────────────────────────────────────── */

/** A number you press to go somewhere. The count IS the affordance. */
export function Door({
  count,
  label,
  detail,
  alert = false,
  onPress,
}: {
  count: ReactNode;
  label: string;
  /**
   * The supporting half-sentence. Two screens had a state strip whose cells each carried
   * one, found nowhere to put it, and dropped or rehomed the lot.
   */
  detail?: string;
  alert?: boolean;
  onPress?: () => void;
}): ReactElement {
  return (
    <button type="button" className="k-door" onClick={onPress}>
      <span className="n" data-alert={alert ? 'true' : undefined}>{count}</span>
      <span>{label}</span>
      {detail ? <span className="sub">{detail}</span> : null}
    </button>
  );
}

export function Doors({ children }: { children: ReactNode }): ReactElement {
  return <div className="k-doors">{children}</div>;
}

/* ── what callers hear ────────────────────────────────────────────────────── */

/**
 * The product's whole argument, and the reason it is on the home screen rather than behind a
 * tab: what is quoted here is exactly what a caller gets. Machine-inserted clauses are greyed
 * so nobody mistakes them for something they wrote or can delete.
 */
export function Saying({
  children,
  onHear,
  label = 'Callers are hearing',
}: {
  children: ReactNode;
  onHear?: () => void;
  label?: string;
}): ReactElement {
  return (
    <div className="k-band">
      <div className="k-saying">
        <div>
          <div className="k-lbl">{label}</div>
          <q>{children}</q>
        </div>
        {onHear ? (
          <BigButton size="hear" play onClick={onHear}>
            Hear it
          </BigButton>
        ) : null}
      </div>
    </div>
  );
}

/* ── sheets ───────────────────────────────────────────────────────────────── */

export type SheetProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Two buttons at most. The right-hand one commits. */
  footer?: ReactNode;
  /**
   * 'wide' is for a sheet holding something that genuinely cannot be narrowed — a table of
   * charges whose Detail column is the reason you opened it. Three screens fell back to the
   * console Modal to get it, which is the one place those screens left the kiosk layer.
   */
  size?: 'default' | 'wide';
};

export function Sheet({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'default',
}: SheetProps): ReactElement | null {
  const ref = useRef<HTMLDivElement>(null);

  /**
   * THE CLOSE HANDLER IS HELD IN A REF, AND THAT IS THE WHOLE POINT.
   *
   * This effect used to depend on [open, onClose]. Every call site passes an inline arrow,
   * which is a new function identity on every render, so the effect re-ran on every render —
   * and re-running it calls `ref.current.focus()`, which yanks the caret out of whatever is
   * being typed into. The symptom: a text field inside a sheet accepts exactly one character.
   * Two agents hit it independently on different screens (the go-live name challenge and the
   * manual's search box) and each worked around it in their own file. It is fixed here, once,
   * for every sheet that will ever hold a field.
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    // Focus moves into the sheet so a keyboard or a screen reader lands where the eye does.
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="k-scrim"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="k-sheet"
        data-size={size === 'default' ? undefined : size}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
      >
        <div className="k-sheet-hd">
          <b>{title}</b>
          {subtitle ? <small>{subtitle}</small> : null}
        </div>
        <div className="k-sheet-bd">{children}</div>
        {footer ? <div className="k-sheet-ft">{footer}</div> : null}
      </div>
    </div>
  );
}

/* ── controls ─────────────────────────────────────────────────────────────── */

/**
 * Up and down, with the value big enough to read from a metre away. Steppers rather than a
 * text field because the common case is "about four hours from now" and nobody wants to type
 * a timestamp at 2 AM with one thumb.
 */
export function BigStepper({
  value,
  detail,
  onDown,
  onUp,
  downLabel = 'Earlier',
  upLabel = 'Later',
}: {
  value: string;
  detail?: string;
  onDown: () => void;
  onUp: () => void;
  downLabel?: string;
  upLabel?: string;
}): ReactElement {
  return (
    <div className="k-step">
      <button type="button" aria-label={downLabel} onClick={onDown}>−</button>
      <span className="val">
        {value}
        {detail ? <small>{detail}</small> : null}
      </span>
      <button type="button" aria-label={upLabel} onClick={onUp}>+</button>
    </div>
  );
}

/** How long something stays on. Named stops, because "6 hours" beats a raw number. */
export function DurationSlider({
  stops,
  index,
  onChange,
  label = 'Turns itself off after',
}: {
  stops: readonly string[];
  index: number;
  onChange: (index: number) => void;
  label?: string;
}): ReactElement {
  return (
    <div className="k-slider">
      <div className="row">
        <span>{label}</span>
        <b>{stops[index]}</b>
      </div>
      <input
        type="range"
        min={0}
        max={stops.length - 1}
        step={1}
        value={index}
        aria-label={label}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <div className="row">
        {stops.map((s) => (
          <span key={s}>{s}</span>
        ))}
      </div>
    </div>
  );
}

/**
 * The escape hatch, not the default. Offered when somebody wants an exact figure the stepper
 * would take a dozen presses to reach.
 */
export function Keypad({
  readout,
  onDigit,
  onBack,
}: {
  readout: string;
  onDigit: (d: string) => void;
  onBack: () => void;
}): ReactElement {
  return (
    <>
      <div className="k-readout">{readout}</div>
      <div className="k-pad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button key={d} type="button" onClick={() => onDigit(d)}>
            {d}
          </button>
        ))}
        <button type="button" className="wide" onClick={() => onDigit('0')}>0</button>
        <button type="button" aria-label="Delete" onClick={onBack}>⌫</button>
      </div>
    </>
  );
}

/** A quotation inside a sheet — what a caller will hear if you press the button below it. */
export function Words({ children, note }: { children: ReactNode; note?: string }): ReactElement {
  return (
    <>
      <div className="k-words">
        <q>{children}</q>
      </div>
      {note ? <p className="k-note">{note}</p> : null}
    </>
  );
}

/* ── lists ────────────────────────────────────────────────────────────────── */

/**
 * What replaces a table. A row is a whole pressable object with one heading, one line of
 * context and a time — not eight columns to read across.
 */
export function ListItem({
  title,
  detail,
  when,
  tone,
  onPress,
  action,
}: {
  title: ReactNode;
  detail?: ReactNode;
  when?: ReactNode;
  tone?: 'need' | 'done';
  onPress?: () => void;
  /**
   * One control on the row itself. Three screens wanted it and all three gave up the same
   * thing — a one-press Acknowledge or Resolve became row, then sheet, then commit.
   *
   * A button cannot contain a button, so a row that carries an action stops being one and
   * becomes a plane with a full-bleed hit area underneath it. The action sits above that
   * area, so pressing the action does not also open the row.
   */
  action?: ReactNode;
}): ReactElement {
  if (!action) {
    return (
      <button type="button" className="k-item" data-tone={tone} onClick={onPress}>
        <span className="rule" aria-hidden="true" />
        <span>
          <b>{title}</b>
          {detail ? <span>{detail}</span> : null}
        </span>
        {when ? <span className="when">{when}</span> : null}
      </button>
    );
  }

  return (
    <div className="k-item" data-tone={tone} data-has-action="true">
      <span className="rule" aria-hidden="true" />
      {onPress ? <button type="button" className="k-item-hit" aria-label="Open" onClick={onPress} /> : null}
      <span>
        <b>{title}</b>
        {detail ? <span>{detail}</span> : null}
      </span>
      {when ? <span className="when">{when}</span> : null}
      <span className="k-item-act">{action}</span>
    </div>
  );
}

export function List({ children }: { children: ReactNode }): ReactElement {
  return <div className="k-list">{children}</div>;
}
